// Podcast publish pipeline — Admin one-drop publish page (spec §2).
//
// Single linear flow: create a draft episode row → pick an mp4 → validate client-side → extract
// audio with ffmpeg.wasm (single-thread core, STREAM COPY, no re-encode) → resumable-upload both
// artifacts (mp4 → private podcast-src, m4a → public podcast-media) → patch the row + enqueue the
// YouTube publish job → list every episode with per-target status chips and a Retry button.
//
// LinkedIn leg (feat/podcast-linkedin, m29): once the YouTube job finishes, podcast-publish-
// youtube auto-enqueues a publish_linkedin job (iff linkedin_enabled). This page surfaces a
// per-episode enable toggle, an editable copy override (placeholder shows the live template
// preview), a status chip + "view on LinkedIn" link once posted, and a manual retry. It also
// shows a channel-level token-expiry warning banner (podcast_channel.linkedin_token_expires_at).
//
// `podcast_publish_episodes` / `podcast_channel` (m28/m29) are not in the generated Supabase
// types yet, so every access casts the client to `any` at the call site (same discipline as
// usePodcastAudio.ts / useBriefAudio.ts). Regenerate types.ts once the migrations land and
// switch to the typed `.from()` call then.
//
// ffmpeg.wasm loading (spec §2.3): dynamic-imported ONLY inside the extract handler below — never
// at module top level — so the public marketing bundle is completely unaffected (same discipline
// as resumableUpload.ts's native imports). We use the SINGLE-THREAD `@ffmpeg/core` build, which
// does not require SharedArrayBuffer / COOP+COEP, and load its wasm/js core from this app's own
// origin (`/ffmpeg/`, copied into `public/ffmpeg/` at build time — see package.json's
// `postinstall:ffmpeg-core` script) rather than a CDN, so it keeps working even if a future CSP
// adds `script-src 'self'` / `worker-src 'self'`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, Mic2, RefreshCw, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { uploadResumable } from '@/lib/native/resumableUpload';

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

type PublishTargetState = 'draft' | 'uploading' | 'published' | 'failed';

interface PublishStatus {
  storage: PublishTargetState;
  youtube: PublishTargetState;
  rss: PublishTargetState;
  // Missing on rows created before m29 — normalisePublishStatus() below always fills it in as
  // 'draft' when absent, per m29_podcast_linkedin.sql's note that existing rows keep the old
  // default and the key must be treated as 'draft' when missing.
  linkedin: PublishTargetState;
}

interface EpisodeRow {
  id: string;
  title: string;
  description: string | null;
  episode_number: number | null;
  season_number: number | null;
  explicit: boolean;
  audio_path: string | null;
  video_path: string | null;
  duration_sec: number | null;
  youtube_video_id: string | null;
  publish_status: PublishStatus;
  yt_error: string | null;
  created_at: string;
  linkedin_copy: string | null;
  linkedin_post_urn: string | null;
  linkedin_enabled: boolean;
  li_retry_count: number;
}

interface ChannelRow {
  spotify_show_url: string | null;
  linkedin_author_urn: string | null;
  linkedin_token_expires_at: string | null;
}

const DEFAULT_PUBLISH_STATUS: PublishStatus = { storage: 'draft', youtube: 'draft', rss: 'draft', linkedin: 'draft' };

function normalisePublishStatus(raw: unknown): PublishStatus {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    const pick = (k: keyof PublishStatus): PublishTargetState => {
      const v = r[k];
      return v === 'uploading' || v === 'published' || v === 'failed' ? v : 'draft';
    };
    // linkedin: absent on any row created before m29 — pick() already treats a missing key as
    // 'draft' (falls through the === checks), matching the migration's documented contract.
    return { storage: pick('storage'), youtube: pick('youtube'), rss: pick('rss'), linkedin: pick('linkedin') };
  }
  return { ...DEFAULT_PUBLISH_STATUS };
}

function mapEpisodeRow(r: Record<string, unknown>): EpisodeRow {
  return {
    id: String(r.id),
    title: (r.title as string | null) ?? '',
    description: (r.description as string | null) ?? null,
    episode_number: typeof r.episode_number === 'number' ? r.episode_number : null,
    season_number: typeof r.season_number === 'number' ? r.season_number : null,
    explicit: !!r.explicit,
    audio_path: (r.audio_path as string | null) ?? null,
    video_path: (r.video_path as string | null) ?? null,
    duration_sec: typeof r.duration_sec === 'number' ? r.duration_sec : null,
    youtube_video_id: (r.youtube_video_id as string | null) ?? null,
    publish_status: normalisePublishStatus(r.publish_status),
    yt_error: (r.yt_error as string | null) ?? null,
    created_at: String(r.created_at ?? new Date().toISOString()),
    linkedin_copy: (r.linkedin_copy as string | null) ?? null,
    linkedin_post_urn: (r.linkedin_post_urn as string | null) ?? null,
    // Column default is true — only an explicit `false` opts an episode out.
    linkedin_enabled: r.linkedin_enabled !== false,
    li_retry_count: typeof r.li_retry_count === 'number' ? r.li_retry_count : 0,
  };
}

/** Trim a description down to roughly 1-2 sentences — mirrors trimDescription() in
 * supabase/functions/podcast-publish-linkedin/index.ts, kept in sync by hand since this is a
 * client-side preview only (the edge function is authoritative for what actually gets posted). */
function trimDescriptionPreview(description: string | null | undefined): string {
  if (!description) return '';
  const trimmed = description.trim();
  if (!trimmed) return '';
  const sentences = trimmed.match(/[^.!?]+[.!?]+(\s+|$)/g) ?? [trimmed];
  const firstTwo = sentences.slice(0, 2).join('').trim();
  return firstTwo || trimmed.slice(0, 280);
}

/** Live preview of the generated LinkedIn post template — same shape as composeLinkedInCopy()
 * in supabase/functions/podcast-publish-linkedin/index.ts. Used as the textarea placeholder so
 * Sam can see exactly what will be posted if linkedin_copy is left blank. */
function composeLinkedInTemplatePreview(
  ep: Pick<EpisodeRow, 'title' | 'description' | 'youtube_video_id'>,
  spotifyShowUrl: string | null | undefined,
): string {
  const desc = trimDescriptionPreview(ep.description);
  const lines: string[] = [];
  lines.push(ep.title || '(untitled episode)');
  if (desc) lines.push(desc);
  lines.push('');
  lines.push(
    `▶ Watch: https://www.youtube.com/watch?v=${ep.youtube_video_id ?? '<pending YouTube upload>'}`,
  );
  if (spotifyShowUrl) lines.push(`🎧 Listen: ${spotifyShowUrl}`);
  return lines.join('\n').trim();
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / (24 * 60 * 60 * 1000));
}

// Client-side guard: reject anything over 40 minutes before any ffmpeg work happens.
const MAX_DURATION_SEC = 40 * 60;
// Soft warn threshold — the TUS path handles large files, this only flags runaway inputs.
const SOFT_SIZE_WARN_BYTES = 4 * 1024 * 1024 * 1024; // ~4 GB

const PUBLIC_URL = 'https://ewyfhrjexfrmcjhqzsmm.supabase.co';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PodcastAdmin() {
  // ---- Episode list -------------------------------------------------------------------------
  const [episodes, setEpisodes] = useState<EpisodeRow[]>([]);
  const [listLoading, setListLoading] = useState(true);

  const fetchEpisodes = useCallback(async () => {
    setListLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('podcast_publish_episodes')
        .select(
          'id, title, description, episode_number, season_number, explicit, audio_path, video_path, duration_sec, youtube_video_id, publish_status, yt_error, created_at, linkedin_copy, linkedin_post_urn, linkedin_enabled, li_retry_count',
        )
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      const rows = (Array.isArray(data) ? data : []) as Record<string, unknown>[];
      setEpisodes(rows.map(mapEpisodeRow));
    } catch (err) {
      console.error('PodcastAdmin: failed to load episodes', err);
      toast.error('Could not load episodes. The podcast_publish_episodes migration may not be applied yet.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchEpisodes();
  }, [fetchEpisodes]);

  // Poll while any episode has a YouTube or LinkedIn job in flight, so the status chip and
  // progress note update as research-job-runner ticks through podcast-publish-youtube /
  // podcast-publish-linkedin.
  useEffect(() => {
    const anyInFlight = episodes.some(
      (e) => e.publish_status.youtube === 'uploading' || e.publish_status.linkedin === 'uploading',
    );
    if (!anyInFlight) return;
    const id = setInterval(() => void fetchEpisodes(), 8000);
    return () => clearInterval(id);
  }, [episodes, fetchEpisodes]);

  // ---- Channel config (Spotify show URL, LinkedIn author urn + token expiry) -----------------
  const [channel, setChannel] = useState<ChannelRow | null>(null);

  const fetchChannel = useCallback(async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('podcast_channel')
        .select('spotify_show_url, linkedin_author_urn, linkedin_token_expires_at')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setChannel({
          spotify_show_url: (data as Record<string, unknown>).spotify_show_url as string | null,
          linkedin_author_urn: (data as Record<string, unknown>).linkedin_author_urn as string | null,
          linkedin_token_expires_at: (data as Record<string, unknown>).linkedin_token_expires_at as string | null,
        });
      }
    } catch (err) {
      console.error('PodcastAdmin: failed to load podcast_channel', err);
    }
  }, []);

  useEffect(() => {
    void fetchChannel();
  }, [fetchChannel]);

  const tokenExpiryDays = useMemo(
    () => daysUntil(channel?.linkedin_token_expires_at ?? null),
    [channel],
  );
  const tokenExpiryWarning = tokenExpiryDays != null && tokenExpiryDays <= 14;

  // ---- New-episode form ----------------------------------------------------------------------
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [seasonNumber, setSeasonNumber] = useState('1');
  const [explicit, setExplicit] = useState(false);
  const [artworkFile, setArtworkFile] = useState<File | null>(null);

  const [draftId, setDraftId] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);

  const createDraft = useCallback(async () => {
    if (!title.trim()) {
      toast.error('Give the episode a title before creating a draft.');
      return;
    }
    setCreatingDraft(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const insert: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim() || null,
        episode_number: episodeNumber.trim() ? Number(episodeNumber.trim()) : null,
        season_number: seasonNumber.trim() ? Number(seasonNumber.trim()) : 1,
        explicit,
        created_by: auth?.user?.id ?? null,
      };
      const { data, error } = await (supabase as any)
        .from('podcast_publish_episodes')
        .insert(insert)
        .select('id')
        .single();
      if (error) throw error;
      setDraftId(String((data as Record<string, unknown>).id));
      toast.success('Draft created — pick the mp4 to continue.');
      void fetchEpisodes();
    } catch (err) {
      console.error('PodcastAdmin: failed to create draft', err);
      toast.error('Could not create the draft row. Check the podcast_publish_episodes migration is applied.');
    } finally {
      setCreatingDraft(false);
    }
  }, [title, description, episodeNumber, seasonNumber, explicit, fetchEpisodes]);

  // ---- mp4 pick + validate --------------------------------------------------------------------
  const [mp4File, setMp4File] = useState<File | null>(null);
  const [probedDuration, setProbedDuration] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onPickMp4 = useCallback((file: File | null) => {
    setMp4File(null);
    setProbedDuration(null);
    if (!file) return;

    const looksLikeMp4 =
      file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');
    if (!looksLikeMp4) {
      toast.error('Please choose an .mp4 file.');
      return;
    }

    if (file.size > SOFT_SIZE_WARN_BYTES) {
      toast.warning(
        `That file is ${(file.size / 1024 ** 3).toFixed(1)} GB — this will take a while to upload. Continuing anyway.`,
      );
    }

    // Cheap duration probe via a throwaway <video> element — NO ffmpeg work yet.
    const url = URL.createObjectURL(file);
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      const dur = probe.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(dur)) {
        // Some containers don't report duration synchronously; don't hard-block on an unknown value.
        setMp4File(file);
        return;
      }
      if (dur > MAX_DURATION_SEC) {
        toast.error(
          `That video is ${Math.round(dur / 60)} minutes — episodes must be 40 minutes or under.`,
        );
        return;
      }
      setProbedDuration(dur);
      setMp4File(file);
    };
    probe.onerror = () => {
      URL.revokeObjectURL(url);
      // Probe failed to read metadata — let it through; ffmpeg's own probe is the real gate.
      setMp4File(file);
    };
    probe.src = url;
  }, []);

  // ---- ffmpeg.wasm audio extract (stream copy) ----------------------------------------------
  const [extracting, setExtracting] = useState(false);
  const [extractNote, setExtractNote] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  const extractAudio = useCallback(async () => {
    if (!mp4File) return;
    setExtracting(true);
    setExtractNote(null);
    setAudioBlob(null);
    try {
      // Lazy-load ffmpeg.wasm — NEVER imported at module top level. Single-thread core (no
      // SharedArrayBuffer / COOP+COEP requirement); core assets served from this app's own
      // origin (public/ffmpeg/), never a CDN.
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile } = await import('@ffmpeg/util');

      const ffmpeg = new FFmpeg();
      await ffmpeg.load({
        coreURL: '/ffmpeg/ffmpeg-core.js',
        wasmURL: '/ffmpeg/ffmpeg-core.wasm',
      });

      const inputName = 'input.mp4';
      const outputName = 'output.m4a';
      await ffmpeg.writeFile(inputName, await fetchFile(mp4File));

      // Preferred path: stream-copy the audio track straight into an m4a container. Only valid
      // when the source audio is AAC-in-MP4 (the overwhelmingly common case for exported mp4).
      let usedFallback = false;
      try {
        await ffmpeg.exec(['-i', inputName, '-vn', '-c:a', 'copy', outputName]);
      } catch (copyErr) {
        // Rare non-AAC audio: the copy mux failed. Fall back to a real encode and say so.
        console.warn('PodcastAdmin: stream-copy mux failed, falling back to AAC re-encode', copyErr);
        usedFallback = true;
        await ffmpeg.exec(['-i', inputName, '-vn', '-c:a', 'aac', '-b:a', '128k', outputName]);
      }

      const data = await ffmpeg.readFile(outputName);
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data as unknown as ArrayBufferLike);
      // TS 5.9's stricter typed-array lib types reject a Uint8Array<ArrayBufferLike>
      // (ffmpeg.wasm may be SharedArrayBuffer-backed) as a BlobPart; runtime is fine.
      const blob = new Blob([bytes as BlobPart], { type: 'audio/mp4' });

      setAudioBlob(blob);
      setAudioDuration(probedDuration);
      setExtractNote(
        usedFallback
          ? 'Audio extracted (re-encoded to AAC — the source audio track was not already AAC).'
          : 'Audio extracted (stream copy — no quality loss).',
      );
      toast.success('Audio extracted.');
    } catch (err) {
      console.error('PodcastAdmin: ffmpeg extract failed', err);
      toast.error('Could not extract audio from that file. Check the console for details.');
    } finally {
      setExtracting(false);
    }
  }, [mp4File, probedDuration]);

  // ---- Dual resumable upload + finalize ------------------------------------------------------
  const [uploadingBoth, setUploadingBoth] = useState(false);
  const [mp4Progress, setMp4Progress] = useState(0);
  const [audioProgress, setAudioProgress] = useState(0);

  const uploadAndPublish = useCallback(async () => {
    if (!draftId) {
      toast.error('Create a draft first.');
      return;
    }
    if (!mp4File || !audioBlob) {
      toast.error('Pick an mp4 and extract audio before uploading.');
      return;
    }

    setUploadingBoth(true);
    setMp4Progress(0);
    setAudioProgress(0);

    try {
      const videoPath = `${draftId}/master.mp4`;
      const audioPath = `${draftId}/audio.m4a`;

      let artworkPath: string | null = null;
      if (artworkFile) {
        const ext = artworkFile.name.split('.').pop() || 'jpg';
        const path = `${draftId}/artwork.${ext}`;
        const { error: artErr } = await supabase.storage
          .from('podcast-media')
          .upload(path, artworkFile, { contentType: artworkFile.type || 'image/jpeg', upsert: true });
        if (artErr) {
          console.error('PodcastAdmin: artwork upload failed (non-fatal)', artErr);
          toast.warning('Artwork upload failed — continuing without it.');
        } else {
          artworkPath = path;
        }
      }

      await Promise.all([
        uploadResumable({
          data: mp4File,
          objectPath: videoPath,
          bucket: 'podcast-src',
          contentType: 'video/mp4',
          onProgress: (sent, total) => setMp4Progress(total > 0 ? (sent / total) * 100 : 0),
        }),
        uploadResumable({
          data: audioBlob,
          objectPath: audioPath,
          bucket: 'podcast-media',
          contentType: 'audio/mp4',
          onProgress: (sent, total) => setAudioProgress(total > 0 ? (sent / total) * 100 : 0),
        }),
      ]);

      const patch: Record<string, unknown> = {
        video_path: videoPath,
        audio_path: audioPath,
        audio_bytes: audioBlob.size,
        audio_mime: 'audio/mp4',
        duration_sec: audioDuration != null ? Math.round(audioDuration) : null,
        rss_included: true,
        publish_status: { storage: 'published', youtube: 'draft', rss: 'published' },
      };
      if (artworkPath) patch.artwork_path = artworkPath;

      const { error: patchErr } = await (supabase as any)
        .from('podcast_publish_episodes')
        .update(patch)
        .eq('id', draftId);
      if (patchErr) throw patchErr;

      // Enqueue the YouTube publish job (research_ingest_jobs is a real, typed table).
      const { error: jobErr } = await supabase.from('research_ingest_jobs').insert({
        job_type: 'publish_youtube',
        payload: { episode_id: draftId } as unknown as never,
        status: 'queued',
      } as never);
      if (jobErr) {
        console.error('PodcastAdmin: failed to enqueue YouTube job', jobErr);
        toast.warning('Storage publish succeeded, but the YouTube job could not be queued. Use Retry below.');
      } else {
        await (supabase as any)
          .from('podcast_publish_episodes')
          .update({ publish_status: { storage: 'published', youtube: 'uploading', rss: 'published' } })
          .eq('id', draftId);
      }

      toast.success('Episode published to storage + RSS. YouTube upload is queued.');

      // Reset the form for the next episode.
      setDraftId(null);
      setTitle('');
      setDescription('');
      setEpisodeNumber('');
      setSeasonNumber('1');
      setExplicit(false);
      setArtworkFile(null);
      setMp4File(null);
      setProbedDuration(null);
      setAudioBlob(null);
      setAudioDuration(null);
      setExtractNote(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      void fetchEpisodes();
    } catch (err) {
      console.error('PodcastAdmin: upload/publish failed', err);
      toast.error('Upload failed. Your draft is saved — reload and retry when ready.');
    } finally {
      setUploadingBoth(false);
    }
  }, [draftId, mp4File, audioBlob, audioDuration, artworkFile, fetchEpisodes]);

  // ---- Retry YouTube (re-enqueue) -------------------------------------------------------------
  const retryYoutube = useCallback(
    async (episodeId: string) => {
      try {
        const { error } = await supabase.from('research_ingest_jobs').insert({
          job_type: 'publish_youtube',
          payload: { episode_id: episodeId } as unknown as never,
          status: 'queued',
        } as never);
        if (error) throw error;
        // Reset yt_retry_count on a manual retry so a previously transient-retry-capped episode
        // (review finding #3) gets a fresh attempt budget rather than immediately re-exceeding
        // the cap on its very first requeued tick.
        await (supabase as any)
          .from('podcast_publish_episodes')
          .update({ publish_status: { youtube: 'uploading' }, yt_error: null, yt_retry_count: 0 })
          .eq('id', episodeId);
        toast.success('YouTube publish re-queued.');
        void fetchEpisodes();
      } catch (err) {
        console.error('PodcastAdmin: retry enqueue failed', err);
        toast.error('Could not re-queue the YouTube job.');
      }
    },
    [fetchEpisodes],
  );

  // ---- LinkedIn: enable toggle, copy override, manual retry -----------------------------------
  const [linkedinDrafts, setLinkedinDrafts] = useState<Record<string, string>>({});
  const [linkedinSaving, setLinkedinSaving] = useState<Record<string, boolean>>({});

  const setLinkedinCopyDraft = useCallback((episodeId: string, value: string) => {
    setLinkedinDrafts((prev) => ({ ...prev, [episodeId]: value }));
  }, []);

  const saveLinkedinCopy = useCallback(
    async (episodeId: string) => {
      const value = linkedinDrafts[episodeId];
      if (value === undefined) return;
      setLinkedinSaving((prev) => ({ ...prev, [episodeId]: true }));
      try {
        const { error } = await (supabase as any)
          .from('podcast_publish_episodes')
          .update({ linkedin_copy: value.trim() ? value : null })
          .eq('id', episodeId);
        if (error) throw error;
        toast.success('LinkedIn copy saved.');
        void fetchEpisodes();
      } catch (err) {
        console.error('PodcastAdmin: failed to save linkedin_copy', err);
        toast.error('Could not save the LinkedIn copy.');
      } finally {
        setLinkedinSaving((prev) => ({ ...prev, [episodeId]: false }));
      }
    },
    [linkedinDrafts, fetchEpisodes],
  );

  const toggleLinkedinEnabled = useCallback(
    async (episodeId: string, enabled: boolean) => {
      try {
        const { error } = await (supabase as any)
          .from('podcast_publish_episodes')
          .update({ linkedin_enabled: enabled })
          .eq('id', episodeId);
        if (error) throw error;
        void fetchEpisodes();
      } catch (err) {
        console.error('PodcastAdmin: failed to toggle linkedin_enabled', err);
        toast.error('Could not update the LinkedIn toggle.');
      }
    },
    [fetchEpisodes],
  );

  const retryLinkedin = useCallback(
    async (episodeId: string) => {
      try {
        const { error } = await supabase.from('research_ingest_jobs').insert({
          job_type: 'publish_linkedin',
          payload: { episode_id: episodeId } as unknown as never,
          status: 'queued',
        } as never);
        if (error) throw error;
        // Reset li_retry_count on a manual retry, same convention as retryYoutube() above — a
        // previously transient-retry-capped episode gets a fresh attempt budget rather than
        // immediately re-exceeding the cap on its very first requeued tick.
        await (supabase as any)
          .from('podcast_publish_episodes')
          .update({ li_retry_count: 0 })
          .eq('id', episodeId);
        toast.success('LinkedIn publish re-queued.');
        void fetchEpisodes();
      } catch (err) {
        console.error('PodcastAdmin: retry enqueue failed', err);
        toast.error('Could not re-queue the LinkedIn job.');
      }
    },
    [fetchEpisodes],
  );

  const canExtract = !!mp4File && !extracting;
  const canUpload = !!draftId && !!mp4File && !!audioBlob && !uploadingBoth;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Mic2 className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold">Podcast publish</h1>
          <p className="text-sm text-muted-foreground">
            One mp4 in → storage, YouTube and the RSS feed all published.
          </p>
        </div>
      </div>

      {tokenExpiryWarning && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {tokenExpiryDays! < 0
            ? 'The LinkedIn access token has expired — LinkedIn posting will fail until it is re-minted.'
            : `The LinkedIn access token expires in ${tokenExpiryDays} day${tokenExpiryDays === 1 ? '' : 's'} — re-mint it soon (see docs/podcast-publish-setup.md).`}
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>1. Episode details</CardTitle>
          <CardDescription>Create a draft row so an interrupted upload survives a reload.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pod-title">Title</Label>
            <Input
              id="pod-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!!draftId}
              placeholder="Episode title"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pod-desc">Description</Label>
            <Textarea
              id="pod-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!!draftId}
              placeholder="Shown in the RSS feed and the YouTube description"
              rows={4}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pod-ep">Episode #</Label>
              <Input
                id="pod-ep"
                type="number"
                value={episodeNumber}
                onChange={(e) => setEpisodeNumber(e.target.value)}
                disabled={!!draftId}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pod-season">Season #</Label>
              <Input
                id="pod-season"
                type="number"
                value={seasonNumber}
                onChange={(e) => setSeasonNumber(e.target.value)}
                disabled={!!draftId}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="pod-explicit" checked={explicit} onCheckedChange={setExplicit} disabled={!!draftId} />
            <Label htmlFor="pod-explicit">Explicit</Label>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pod-artwork">Episode artwork (optional)</Label>
            <Input
              id="pod-artwork"
              type="file"
              accept="image/*"
              onChange={(e) => setArtworkFile(e.target.files?.[0] ?? null)}
              disabled={!!draftId}
            />
          </div>
          {!draftId ? (
            <Button onClick={() => void createDraft()} disabled={creatingDraft || !title.trim()}>
              {creatingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create draft
            </Button>
          ) : (
            <Badge variant="secondary">Draft created — continue below</Badge>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>2. Video file</CardTitle>
          <CardDescription>mp4 only, 40 minutes or under.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            ref={fileInputRef}
            type="file"
            accept="video/mp4"
            disabled={!draftId}
            onChange={(e) => onPickMp4(e.target.files?.[0] ?? null)}
          />
          {mp4File && (
            <p className="text-sm text-muted-foreground">
              {mp4File.name} ({(mp4File.size / 1024 ** 2).toFixed(0)} MB
              {probedDuration != null ? `, ${Math.round(probedDuration / 60)} min` : ''})
            </p>
          )}
          <Button onClick={() => void extractAudio()} disabled={!canExtract} variant="secondary">
            {extracting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Extract audio
          </Button>
          {extractNote && <p className="text-sm text-muted-foreground">{extractNote}</p>}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>3. Upload + publish</CardTitle>
          <CardDescription>
            Uploads the master mp4 (private) and the extracted audio (public enclosure), then queues
            the YouTube publish job.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {uploadingBoth && (
            <div className="space-y-3">
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Video (podcast-src)</p>
                <Progress value={mp4Progress} />
              </div>
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Audio (podcast-media)</p>
                <Progress value={audioProgress} />
              </div>
            </div>
          )}
          <Button onClick={() => void uploadAndPublish()} disabled={!canUpload}>
            {uploadingBoth ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="mr-2 h-4 w-4" />
            )}
            Upload &amp; publish
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Episodes</CardTitle>
          <CardDescription>Per-target publish status. Feed: {PUBLIC_URL}/functions/v1/podcast-feed</CardDescription>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : episodes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No episodes yet.</p>
          ) : (
            <div className="space-y-3">
              {episodes.map((ep) => {
                const templatePreview = composeLinkedInTemplatePreview(ep, channel?.spotify_show_url);
                const copyValue = linkedinDrafts[ep.id] ?? ep.linkedin_copy ?? '';
                const copyDirty = linkedinDrafts[ep.id] !== undefined && linkedinDrafts[ep.id] !== (ep.linkedin_copy ?? '');
                return (
                  <div key={ep.id} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{ep.title || '(untitled)'}</p>
                        {ep.yt_error && (
                          <p className="mt-1 text-xs text-destructive">YouTube error: {ep.yt_error}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                        <StatusChip label="Storage" state={ep.publish_status.storage} />
                        <StatusChip label="YouTube" state={ep.publish_status.youtube} />
                        <StatusChip label="RSS" state={ep.publish_status.rss} />
                        <StatusChip label="LinkedIn" state={ep.publish_status.linkedin} />
                      </div>
                    </div>
                    {ep.publish_status.youtube === 'failed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => void retryYoutube(ep.id)}
                      >
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        Retry YouTube
                      </Button>
                    )}

                    <div className="mt-3 space-y-2 border-t pt-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`li-enabled-${ep.id}`}
                            checked={ep.linkedin_enabled}
                            onCheckedChange={(checked) => void toggleLinkedinEnabled(ep.id, checked)}
                          />
                          <Label htmlFor={`li-enabled-${ep.id}`} className="text-sm">
                            Post to LinkedIn
                          </Label>
                        </div>
                        {ep.publish_status.linkedin === 'published' && ep.linkedin_post_urn && (
                          <a
                            href={`https://www.linkedin.com/feed/update/${ep.linkedin_post_urn}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-primary underline underline-offset-2"
                          >
                            View on LinkedIn
                          </a>
                        )}
                      </div>

                      <Textarea
                        value={copyValue}
                        onChange={(e) => setLinkedinCopyDraft(ep.id, e.target.value)}
                        placeholder={templatePreview}
                        rows={4}
                        disabled={!ep.linkedin_enabled}
                        className="text-sm"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">
                          Leave blank to auto-post the template shown above.
                        </p>
                        <div className="flex shrink-0 gap-1.5">
                          {copyDirty && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => void saveLinkedinCopy(ep.id)}
                              disabled={!!linkedinSaving[ep.id]}
                            >
                              {linkedinSaving[ep.id] ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                              Save copy
                            </Button>
                          )}
                          {ep.publish_status.linkedin === 'failed' && (
                            <Button size="sm" variant="outline" onClick={() => void retryLinkedin(ep.id)}>
                              <RefreshCw className="mr-2 h-3.5 w-3.5" />
                              Retry LinkedIn
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusChip({ label, state }: { label: string; state: PublishTargetState }) {
  const variant = state === 'published' ? 'default' : state === 'failed' ? 'destructive' : 'secondary';
  const text = state === 'uploading' ? 'Uploading…' : state.charAt(0).toUpperCase() + state.slice(1);
  return (
    <Badge variant={variant} className="whitespace-nowrap text-[11px]">
      {label}: {text}
    </Badge>
  );
}
